import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Play,
  X,
  Clock3,
  Download,
  Link2,
  Check,
  Loader2,
} from 'lucide-react';

type VideoItem = {
  id: string;
  title: string;
  category: string;
  duration: string;
  resolution: string;
  thumbnail: string;
  videoUrl: string | null; // null = ainda em processamento na origem
  synopsis: string;
  highlights: string[];
};

const CATEGORY_ACCENT: Record<string, string> = {
  'Jornada Completa': '#690093',
  'Conceito ERP': '#4C2FB8',
  'Destaque IA': '#0B5FA8',
  'Demonstrações Individuais': '#00768C',
  'Lukos Pay & TEF': '#B45309',
  'Whiteboard TV': '#334155',
};

const CATEGORIES = [
  'Todos',
  'Jornada Completa',
  'Conceito ERP',
  'Destaque IA',
  'Demonstrações Individuais',
  'Lukos Pay & TEF',
  'Whiteboard TV',
];

const VIDEOS: VideoItem[] = [
  {
    id: 'jornada-lukia-evento',
    title: 'Jornada Completa — Edição Especial Expopostos',
    category: 'Jornada Completa',
    duration: '12m 30s',
    resolution: '1080p',
    thumbnail: '/videos-hub/thumbnails/jornada-lukia-evento.jpg',
    videoUrl: null, // origem estava sendo atualizada no momento do download — repor depois
    synopsis:
      'Apresentação mestra do ecossistema Lukos e da Luk IA ao vivo, com demonstração conversacional do ERP e fechamento executivo.',
    highlights: [
      'Perguntas em linguagem natural conectadas ao ERP ao vivo',
      'Geração instantânea de painéis gerenciais cruzando módulos',
      'Fechamento com QR Code interativo para captação de leads',
    ],
  },
  {
    id: 'lukos-conceito-erp',
    title: 'Conceito — Quando o ERP Começa a Entender Você',
    category: 'Conceito ERP',
    duration: '2m 15s',
    resolution: '1080p',
    thumbnail: '/videos-hub/thumbnails/lukos-conceito-erp.jpg',
    videoUrl: '/videos-hub/videos/lukos-conceito-erp.mp4',
    synopsis:
      'A transição dos ERPs para postos e conveniência: do modelo antigo de menus complexos para sistemas parceiros com governança e auditoria total.',
    highlights: [
      'Superação da curva de aprendizado de ERPs legados',
      'Transformação de dados brutos em decisões acionáveis',
      'Inteligência com permissões e trilha de auditoria',
    ],
  },
  {
    id: 'destaque-ia-pergunte-numeros',
    title: 'Destaque IA — Pergunte Seus Números ao ERP',
    category: 'Destaque IA',
    duration: '3m 30s',
    resolution: '1080p',
    thumbnail: '/videos-hub/thumbnails/serie-lukia-horizontal.jpg',
    videoUrl: '/videos-hub/videos/serie-lukia-horizontal.mp4',
    synopsis:
      'Como o Luk interpreta perguntas estratégicas, mantém contexto entre dúvidas e cruza módulos de vendas e financeiro automaticamente.',
    highlights: [
      'Sem necessidade de abrir telas ou selecionar filtros manuais',
      'Cálculo inteligente de média diária e sazonalidade',
      'Visão unificada da pista ao caixa sem trocar de sistema',
    ],
  },
  {
    id: 'demonstracao-resumo-caixa',
    title: 'Demonstração — Resumo de Caixa & Fechamento de Turno',
    category: 'Demonstrações Individuais',
    duration: '3m 02s',
    resolution: 'HD',
    thumbnail: '/videos-hub/thumbnails/demonstracao-resumo-caixa.jpg',
    videoUrl: '/videos-hub/videos/demonstracao-resumo-caixa.mp4',
    synopsis:
      'Rotina de fechamento de turno no PDV com assistência do Luk: conferência cega de valores, sangrias, conciliação e auditoria em segundos.',
    highlights: [
      'Conferência cega e fechamento assistido por IA',
      'Identificação imediata de divergências e sangrias',
      'Trilha de auditoria transparente para a gerência',
    ],
  },
  {
    id: 'demonstracao-cadastro-produto',
    title: 'Demonstração — Cadastro de Produto com Assistência de IA',
    category: 'Demonstrações Individuais',
    duration: '3m 05s',
    resolution: 'HD',
    thumbnail: '/videos-hub/thumbnails/demonstracao-cadastro-produto.jpg',
    videoUrl: '/videos-hub/videos/demonstracao-cadastro-produto.mp4',
    synopsis:
      'Cadastro e manutenção de produtos assistido por IA: autocompletar de tributações (NCM/CEST), regras fiscais e categorização automática.',
    highlights: [
      'Preenchimento automático e consulta por código de barras',
      'Enquadramento tributário sem erros manuais',
      'Agilidade no cadastro de novos itens da conveniência',
    ],
  },
  {
    id: 'demonstracao-nota-combustivel',
    title: 'Demonstração — Entrada de Nota de Combustível & Vínculo de Bicos',
    category: 'Demonstrações Individuais',
    duration: '2m 45s',
    resolution: 'HD',
    thumbnail: '/videos-hub/thumbnails/demonstracao-nota-combustivel.jpg',
    videoUrl: '/videos-hub/videos/demonstracao-nota-combustivel.mp4',
    synopsis:
      'Importação de XML de combustível: vinculação de tanques, atualização de estoque físico/escritural e conferência para o LMC.',
    highlights: [
      'Leitura direta do XML da distribuidora e conferência de volume',
      'Vinculação direta com tanques e bicos da pista',
      'Lançamento automático para conformidade com o LMC',
    ],
  },
  {
    id: 'demonstracao-nota-produtos',
    title: 'Demonstração — Entrada de Nota de Produtos & Conversão de Embalagens',
    category: 'Demonstrações Individuais',
    duration: '3m 55s',
    resolution: 'HD',
    thumbnail: '/videos-hub/thumbnails/demonstracao-nota-produtos.jpg',
    videoUrl: '/videos-hub/videos/demonstracao-nota-produtos.mp4',
    synopsis:
      'Entrada de mercadorias da conveniência: conversão automática de caixas/fardos em unidades, cálculo de margem e precificação rápida.',
    highlights: [
      'Conversão inteligente de fator de embalagem',
      'Atualização de custos médios e sugestão de margem',
      'Vínculo ágil com o estoque e o PDV da loja',
    ],
  },
  {
    id: 'lukos-pay-fluxo-completo',
    title: 'Lukos Pay — Ecossistema de Pagamentos & Automação de TEF',
    category: 'Lukos Pay & TEF',
    duration: '4m 10s',
    resolution: '1080p',
    thumbnail: '/videos-hub/thumbnails/lukos-pay-fluxo-completo.jpg',
    videoUrl: '/videos-hub/videos/lukos-pay-fluxo-completo.mp4',
    synopsis:
      'Solução integrada de pagamentos e TEF: conciliação de cartões em tempo real, liquidação transparente e integração com a pista e a conveniência.',
    highlights: [
      'Eliminação de divergências de taxas de adquirentes',
      'Processamento veloz no PDV sem travas de comunicação',
      'Conciliação automática integrada à contabilidade',
    ],
  },
  {
    id: 'whiteboard-tv-completo',
    title: 'Apresentação Whiteboard TV — Demonstração Executiva',
    category: 'Whiteboard TV',
    duration: '5m 45s',
    resolution: '1080p',
    thumbnail: '/videos-hub/thumbnails/whiteboard-tv-completo.jpg',
    videoUrl: '/videos-hub/videos/whiteboard-tv-completo.mp4',
    synopsis:
      'Vídeo animado em estilo quadro branco, feito para TVs de estande, showrooms e apresentações institucionais para redes de postos.',
    highlights: [
      'Formato dinâmico de alta retenção visual',
      'Legendas sincronizadas para ambientes com ou sem som',
      'Síntese completa dos benefícios de negócio Lukos',
    ],
  },
];

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

export default function LukosVideoHub() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('Todos');
  const [active, setActive] = useState<VideoItem | null>(null);
  const [speed, setSpeed] = useState(1);
  const [copied, setCopied] = useState(false);
  // some enquanto o vídeo ainda não começou a tocar de fato — sem isso o clique
  // abre o modal e mostra um retângulo preto até o arquivo (10-20MB) bufferizar
  const [buffering, setBuffering] = useState(true);
  const lastTriggerRef = useRef<HTMLElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return VIDEOS.filter((v) => {
      const matchesCategory = category === 'Todos' || v.category === category;
      const matchesQuery =
        !q ||
        v.title.toLowerCase().includes(q) ||
        v.synopsis.toLowerCase().includes(q) ||
        v.category.toLowerCase().includes(q);
      return matchesCategory && matchesQuery;
    });
  }, [query, category]);

  const openVideo = useCallback((video: VideoItem, e: React.MouseEvent<HTMLElement>) => {
    if (!video.videoUrl) return;
    lastTriggerRef.current = e.currentTarget;
    setSpeed(1);
    setCopied(false);
    setBuffering(true);
    setActive(video);
  }, []);

  const closeVideo = useCallback(() => {
    setActive(null);
    // devolve o foco pro card que abriu o modal — sem isso o teclado/leitor de
    // tela "perde o lugar" e volta pro topo da página
    lastTriggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeVideo();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtnRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [active, closeVideo]);

  const copyLink = useCallback(async () => {
    if (!active) return;
    const url = `${window.location.origin}${window.location.pathname}#video-${active.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard indisponível (ex.: contexto não seguro) — ignora silenciosamente
    }
  }, [active]);

  return (
    <section className="py-4">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-8 max-w-2xl"
      >
        <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.28em] text-[#690093]">
          •&nbsp;&nbsp;PORTFÓLIO EM VÍDEO
        </p>
        <h2 className="text-[24px] font-extrabold leading-[1.1] tracking-tight text-slate-900 sm:text-[30px]">
          Veja o Lukos <span className="text-[#690093]">em ação.</span>
        </h2>
        <p className="mt-3 text-[13.5px] leading-[1.6] text-slate-500">
          Demonstrações reais do ERP, da Luk IA e do Lukos Pay — do cadastro de produto ao fechamento de turno.
        </p>
      </motion.div>

      {/* busca + filtros */}
      <div className="mb-8 flex flex-col gap-4">
        <div className="relative max-w-md">
          <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar vídeos, demonstrações ou operações..."
            className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-11 pr-4 text-[13.5px] text-slate-700 placeholder:text-slate-400 focus:border-[#690093]/40 focus:outline-none focus:ring-2 focus:ring-[#690093]/15"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((cat) => {
            const isActive = category === cat;
            const accent = CATEGORY_ACCENT[cat];
            return (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                style={isActive && accent ? { backgroundColor: accent, borderColor: accent } : undefined}
                className={`rounded-full border px-4 py-1.5 text-[12px] font-semibold transition-colors ${
                  isActive
                    ? 'border-transparent text-white' + (accent ? '' : ' bg-[#690093]')
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        <p className="text-[12px] font-medium text-slate-400">
          {filtered.length} {filtered.length === 1 ? 'vídeo' : 'vídeos'}
        </p>
      </div>

      {/* grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((v, i) => {
          const accent = CATEGORY_ACCENT[v.category] ?? '#690093';
          const pending = !v.videoUrl;
          return (
            <motion.button
              key={v.id}
              type="button"
              onClick={(e) => openVideo(v, e)}
              disabled={pending}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 6) * 0.05, duration: 0.4 }}
              className={`group relative aspect-video overflow-hidden rounded-2xl bg-slate-900 text-left ${
                pending ? 'cursor-default' : 'cursor-pointer'
              }`}
            >
              <img
                src={v.thumbnail}
                alt=""
                aria-hidden
                loading="lazy"
                className={`absolute inset-0 h-full w-full object-cover transition-transform duration-700 ${
                  pending ? 'opacity-50 grayscale' : 'opacity-90 group-hover:scale-105'
                }`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />

              <span
                className="absolute left-3 top-3 inline-flex items-center rounded-full px-3 py-1 text-[9.5px] font-bold uppercase tracking-[0.1em] text-white ring-1 ring-white/15"
                style={{ backgroundColor: `${accent}CC` }}
              >
                {v.category}
              </span>

              {pending ? (
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/70 px-3.5 py-2 text-[11px] font-bold text-white">
                    <Clock3 size={13} />
                    Em processamento
                  </span>
                </span>
              ) : (
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform duration-300 group-hover:scale-110">
                    <Play size={18} className="ml-0.5 fill-[#690093] text-[#690093]" />
                  </span>
                </span>
              )}

              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-3.5">
                <h3 className="text-[13px] font-bold leading-tight text-white [text-shadow:0_1px_6px_rgba(0,0,0,0.6)]">
                  {v.title}
                </h3>
                <span className="shrink-0 rounded-md bg-slate-950/60 px-2 py-1 text-[10px] font-semibold text-white/90">
                  {v.duration}
                </span>
              </div>
            </motion.button>
          );
        })}

        {filtered.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-slate-200 py-16 text-center text-[13.5px] text-slate-400">
            Nenhum vídeo encontrado para essa busca.
          </div>
        )}
      </div>

      {/* modal cinema */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
            onClick={closeVideo}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label={active.title}
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            >
              <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4 ">
                <div className="min-w-0">
                  <h3 className="truncate text-[15px] font-bold text-slate-900">{active.title}</h3>
                  <span
                    className="mt-1 inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
                    style={{ backgroundColor: CATEGORY_ACCENT[active.category] ?? '#690093' }}
                  >
                    {active.category}
                  </span>
                </div>
                <button
                  ref={closeBtnRef}
                  onClick={closeVideo}
                  aria-label="Fechar"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="relative bg-black">
                <video
                  key={active.id}
                  src={active.videoUrl ?? undefined}
                  poster={active.thumbnail}
                  controls
                  autoPlay
                  playsInline
                  preload="auto"
                  className="max-h-[52vh] w-full"
                  onPlaying={() => setBuffering(false)}
                  onWaiting={() => setBuffering(true)}
                  ref={(el) => {
                    if (!el) return;
                    el.playbackRate = speed;
                    // o autoPlay sozinho às vezes não dispara a tempo do primeiro
                    // frame pós-clique — chamar play() aqui garante que comece
                    el.play().catch(() => {});
                  }}
                />
                {buffering && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <Loader2 size={28} className="animate-spin text-white/80" />
                  </div>
                )}
              </div>

              <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5 sm:flex-row ">
                <div className="flex-1">
                  <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold text-slate-500">
                    <Clock3 size={13} />
                    {active.duration}
                    <span className="text-slate-300">•</span>
                    {active.resolution}
                  </div>
                  <p className="text-[13px] leading-[1.65] text-slate-600">{active.synopsis}</p>

                  <h4 className="mb-2 mt-4 text-[10.5px] font-bold uppercase tracking-[0.1em] text-slate-400">
                    Destaques
                  </h4>
                  <ul className="space-y-1.5">
                    {active.highlights.map((h) => (
                      <li key={h} className="flex gap-2 text-[12.5px] leading-snug text-slate-600">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#690093]" />
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex shrink-0 flex-row gap-2 sm:w-40 sm:flex-col">
                  <label className="text-[11px] font-semibold text-slate-500 sm:block">
                    Velocidade
                    <select
                      value={speed}
                      onChange={(e) => setSpeed(Number(e.target.value))}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-[12.5px] text-slate-700"
                    >
                      {SPEEDS.map((s) => (
                        <option key={s} value={s}>
                          {s}x
                        </option>
                      ))}
                    </select>
                  </label>

                  <button
                    onClick={copyLink}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-[12px] font-semibold text-slate-600 hover:bg-slate-50"
                  >
                    {copied ? <Check size={13} /> : <Link2 size={13} />}
                    {copied ? 'Copiado' : 'Copiar link'}
                  </button>

                  <a
                    href={active.videoUrl ?? undefined}
                    download
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#690093] px-3 py-2 text-[12px] font-semibold text-white hover:bg-[#520074]"
                  >
                    <Download size={13} />
                    Baixar
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
